import { InfoCircleOutlined } from '@ant-design/icons'
import {
  Button,
  Checkbox,
  Divider,
  Form,
  FormInstance,
  FormProps,
  message,
  Segmented,
  Select,
} from 'antd'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react'

import {
  CadastreMapDebtFilterFormOutput,
  DebtFilterFormValues,
} from '../../../../../common/cadastreMapDebtTypes'
import {
  debtBooleanOptions,
  debtForeclosureOptions,
  debtFormHelp,
  debtHighlightOptions,
  debtInsolvencyOptions,
  debtUnitUsageOptions,
  getDebtFilterOutput,
} from '../../../../utils/cadastreMapDebtHelpers'
import { formItemLayout } from '../../../../utils/layoutConst'
import { useToggleState } from '../../../../utils/useToggleState'
import { CadastreMapDebtFilterFormRangeFragment } from '../../../CadastreMapDebtFilterFormRangeFragment/CadastreMapDebtFilterFormRangeFragment'
import { Fade } from '../../../Fade/Fade'
import { RuianLayer } from '../../types/RuianLayers'
import { RuianQuery } from '../../util/RuianQuery'

export interface CadastreMapDebtFilterFormProps {
  form: FormInstance<DebtFilterFormValues>
  onSubmit: (values: CadastreMapDebtFilterFormOutput) => void
  onClose?: () => void
}

const initialValues: Partial<DebtFilterFormValues> = {
  exekuce: 'true',
  insolvence: '',
  voluntaryLien: '',
  involuntaryLien: '',
  building: { enabled: true, usage: [] },
  housingUnit: { enabled: true, usage: [] },
  highlight: 0,
  parcel: {
    enabled: false,
    from: undefined,
    to: undefined,
    usage: [],
  },
}

export const CadastreMapDebtFilterForm: FunctionComponent<CadastreMapDebtFilterFormProps> =
  props => {
    const [showHelp, toggleShowHelp] = useToggleState()
    const [buildingUsageOptions, setBuildingUsageOptions] = useState<
      { value: string; label: string }[]
    >([])
    useEffect(() => {
      fetchLandUsageOptions()
      async function fetchLandUsageOptions() {
        try {
          const { domains } = await RuianQuery.queryDomains(
            RuianLayer.STAVEBNI_OBJEKT
          )
          const buildingUsageDomain = domains.find(
            domain => domain.name === 'WS2_VFR_ZpusobVyuzitiObjektu'
          )
          setBuildingUsageOptions(
            buildingUsageDomain?.codedValues.map(codedValue => ({
              value: codedValue.code.toString(),
              label: codedValue.name,
            })) ?? []
          )
        } catch (error) {
          message.error(
            'Nepodařilo se načíst způsoby využití budov pro formulář.'
          )
          setBuildingUsageOptions([])
        }
      }
    }, [])

    const [parcelUsageOptions, setParcelUsageOptions] = useState<
      { value: string; label: string }[]
    >([])

    useEffect(() => {
      fetchLandUsageOptions()
      async function fetchLandUsageOptions() {
        try {
          const { domains } = await RuianQuery.queryDomains(RuianLayer.PARCELA)
          const landUsageDomain = domains.find(
            domain => domain.name === 'WS2_VFR_ZpusobVyuzitiPozemku'
          )
          setParcelUsageOptions(
            landUsageDomain?.codedValues.map(codedValue => ({
              value: codedValue.code.toString(),
              label: codedValue.name,
            })) ?? []
          )
        } catch (error) {
          message.error(
            'Nepodařilo se načíst způsoby využití pozemku pro formulář.'
          )
          setParcelUsageOptions([])
        }
      }
    }, [])

    const onFinish = useCallback<
      NonNullable<FormProps<DebtFilterFormValues>['onFinish']>
    >(
      values => {
        const output = getDebtFilterOutput(values)
        props.onSubmit(output as CadastreMapDebtFilterFormOutput)
      },
      [props]
    )

    const buildingEnabled = Form.useWatch(['building', 'enabled'], props.form)
    const parcelEnabled = Form.useWatch(['parcel', 'enabled'], props.form)
    const housingUnitEnabled = Form.useWatch(
      ['housingUnit', 'enabled'],
      props.form
    )

    return (
      <Form<DebtFilterFormValues>
        {...formItemLayout}
        className="mt-4"
        form={props.form}
        initialValues={initialValues}
        onFinish={onFinish}
      >
        <Form.Item
          label="Exekuce"
          name="exekuce"
          help={(showHelp && debtFormHelp.exekuce) || undefined}
        >
          <Segmented block options={debtForeclosureOptions} />
        </Form.Item>

        <Form.Item
          label="Insolvence"
          name="insolvence"
          help={(showHelp && debtFormHelp.insolvence) || undefined}
        >
          <Segmented block options={debtInsolvencyOptions} />
        </Form.Item>

        <Form.Item
          label="Zástavy smluvní"
          name="voluntaryLien"
          help={(showHelp && debtFormHelp.voluntaryLien) || undefined}
        >
          <Segmented block options={debtBooleanOptions} />
        </Form.Item>

        <Form.Item
          label="Zástavy nedobrovolné"
          name="involuntaryLien"
          help={(showHelp && debtFormHelp.involuntaryLien) || undefined}
        >
          <Segmented block options={debtBooleanOptions} />
        </Form.Item>

        <Form.Item
          label="Zvýraznit změny za poslední"
          name="highlight"
          help={(showHelp && debtFormHelp.highlight) || undefined}
        >
          <Segmented block options={debtHighlightOptions} />
        </Form.Item>

        <Form.Item
          label="Počet vlastníků"
          help={(showHelp && debtFormHelp.ownership) || undefined}
          className="!mb-10"
        >
          <CadastreMapDebtFilterFormRangeFragment type="ownership" />
        </Form.Item>

        <Form.Item
          name={['building', 'enabled']}
          label="Budovy"
          valuePropName="checked"
          help={(showHelp && debtFormHelp.building) || undefined}
          className="!mb-1"
        >
          <Checkbox rootClassName="w-full">Zobrazit</Checkbox>
        </Form.Item>

        <Fade show={buildingEnabled}>
          <Form.Item
            name={['building', 'usage']}
            label="Způsoby využití budov"
            help={(showHelp && debtFormHelp.buildingUse) || undefined}
          >
            <Select
              mode="multiple"
              options={buildingUsageOptions}
              placeholder="Způsoby využití"
            />
          </Form.Item>
        </Fade>

        <Form.Item
          name={['housingUnit', 'enabled']}
          label="Jednotky"
          valuePropName="checked"
          help={(showHelp && debtFormHelp.housingUnit) || undefined}
          className="!mb-1"
        >
          <Checkbox rootClassName="w-full">Zobrazit</Checkbox>
        </Form.Item>

        <Fade show={housingUnitEnabled}>
          <Form.Item
            name={['housingUnit', 'usage']}
            label="Způsoby využití jednotek"
            help={(showHelp && debtFormHelp.unitUse) || undefined}
          >
            <Select
              mode="multiple"
              options={debtUnitUsageOptions}
              placeholder="Způsoby využití"
            />
          </Form.Item>
        </Fade>

        <Form.Item
          name={['parcel', 'enabled']}
          label="Parcely"
          valuePropName="checked"
          help={(showHelp && debtFormHelp.parcel) || undefined}
          className="!mb-1"
        >
          <Checkbox rootClassName="w-full">Zobrazit</Checkbox>
        </Form.Item>

        <Fade show={parcelEnabled}>
          <Form.Item
            name={['parcel', 'usage']}
            label="Způsoby využití parcel"
            help={(showHelp && debtFormHelp.parcelUse) || undefined}
          >
            <Select
              mode="multiple"
              options={parcelUsageOptions}
              placeholder="Způsoby využití"
            />
          </Form.Item>

          <Form.Item
            label="Výměra parcely (m²)"
            help={(showHelp && debtFormHelp.parcelSize) || undefined}
          >
            <CadastreMapDebtFilterFormRangeFragment type="parcel" />
          </Form.Item>
        </Fade>

        <Divider />

        <Form.Item noStyle>
          <div className="flex justify-between">
            <Button
              icon={<InfoCircleOutlined />}
              type="link"
              onClick={toggleShowHelp}
            >
              {showHelp ? 'Skrýt nápovědu' : 'Zobrazit nápovědu'}
            </Button>

            <div className="space-x-2">
              <Button type="primary" ghost danger htmlType="reset">
                Resetovat filtr
              </Button>

              <Button onClick={props.onClose}>Zrušit</Button>

              <Button type="primary" htmlType="submit" onClick={props.onClose}>
                Zobrazit nemovitosti
              </Button>
            </div>
          </div>
        </Form.Item>
      </Form>
    )
  }
